import type { Component } from "solid-js";
import { createMemo, Show } from "solid-js";
import { useI18n } from "@/api/i18n";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import type { PlanLimitError } from "../api/types";
import { limitLabel } from "../utils/check-limit";
import { isBillingEnabled } from "../utils/is-billing-enabled";

type PlanLimitModalProps = {
  open: boolean;
  detail: PlanLimitError | null;
  onClose: () => void;
  onUpgrade: () => void;
};

export const PlanLimitModal: Component<PlanLimitModalProps> = (props) => {
  const { t } = useI18n();

  // Guard: exclude unlimited plans (-1) so the modal never renders for them
  const safeDetail = createMemo(() => {
    const d = props.detail;
    if (!d || d.max === -1) return null;
    return d;
  });

  const handleUpgrade = () => {
    props.onUpgrade();
    props.onClose();
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) props.onClose();
  };

  return (
    <Show when={safeDetail()}>
      {(detail) => (
        <Dialog open={props.open} onOpenChange={handleOpenChange}>
          <DialogContent class="sm:max-w-md">
            <DialogHeader>
              <div class="mx-auto mb-4 h-12 w-12 rounded-full bg-orange-100 flex items-center justify-center">
                <p class="sr-only">Lock Icon</p>
                <i class="ph-fill ph-lock text-orange-600 text-2xl" />
              </div>
              <DialogTitle>
                {t("billing.plan_limit_modal.headline")}
              </DialogTitle>
              <DialogDescription>
                {t("billing.plan_limit_modal.body", {
                  current_plan: detail().plan,
                  limit_value: String(detail().max),
                  limit_label: limitLabel(detail().limit),
                  current_usage: String(detail().current),
                })}
              </DialogDescription>
            </DialogHeader>
            <DialogFooter class="flex flex-col sm:flex-row gap-2">
              {/* Senza billing la pagina dei piani non esiste (`BillingGuard`
                  manda a /404): resta la spiegazione, e una sola uscita che
                  non promette un viaggio che finirebbe male. */}
              <Show
                when={isBillingEnabled()}
                fallback={
                  <Button onClick={props.onClose} class="w-full sm:w-auto">
                    {t("billing.plan_limit_modal.acknowledge_cta")}
                  </Button>
                }
              >
                <Button
                  variant="outline"
                  onClick={props.onClose}
                  class="w-full sm:w-auto"
                >
                  {t("billing.plan_limit_modal.dismiss_cta")}
                </Button>
                <Button onClick={handleUpgrade} class="w-full sm:w-auto">
                  {t("billing.plan_limit_modal.upgrade_cta")}
                </Button>
              </Show>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </Show>
  );
};
